/* eslint-disable no-underscore-dangle */
/* eslint-disable comma-dangle */
import React, { useState, useContext } from 'react'
import { Typography, Button, Box, TextField, InputAdornment } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { useQuery } from '@apollo/client'
import { Link as Linkreact } from 'react-router-dom'
import NoteAddTwoToneIcon from '@mui/icons-material/NoteAddTwoTone'

import eventsQuery from '../../graphql/query/events'
import AuthContext from '../../contexts/AuthContext'
import EventsTable from '../../components/events/EventsTable'

const EventsPage = () => {
  const { user } = useContext(AuthContext)
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  let variables = {
    filter: {
      organizerId: user._id,
    },
  }
  if (user.role === 'Admin') {
    variables = {}
  }
  const events = useQuery(eventsQuery, { variables })

  if (user.role === 'Staff') {
    return 'Cannot Access'
  }

  // Filter events based on search query
  const filteredEvents =
    events.loading || events.data === undefined
      ? []
      : events.data.events.filter((event) =>
          event.name.toLowerCase().includes(searchQuery.toLowerCase())
        )

  // Pagination
  const paginatedEvents = filteredEvents.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  // Calculate pagination
  const totalPages = Math.ceil(filteredEvents.length / rowsPerPage)
  const getPageNumbers = () => {
    const pages = []
    const maxVisible = 5

    if (totalPages <= maxVisible) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      // Always show first page
      pages.push(1)

      if (page + 1 <= 3) {
        // Near the start
        for (let i = 2; i <= 4; i++) {
          pages.push(i)
        }
        pages.push('ellipsis')
        pages.push(totalPages)
      } else if (page + 1 >= totalPages - 2) {
        // Near the end
        pages.push('ellipsis')
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i)
        }
      } else {
        // In the middle
        pages.push('ellipsis')
        for (let i = page; i <= page + 2; i++) {
          pages.push(i + 1)
        }
        pages.push('ellipsis')
        pages.push(totalPages)
      }
    }

    return pages
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
      }}>
      {/* Page Header Section */}
      <Box
        sx={{
          mb: { xs: 3, sm: 4, md: 5 },
        }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            mb: { xs: 1, sm: 1.25, md: 1.5 },
            mt: { xs: 1, sm: 1.25, md: 1.5 },
            color: 'text.primary',
            fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
            lineHeight: 1.3,
          }}>
          All Event
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
            fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
            lineHeight: 1.5,
            mb: { xs: 2.5, sm: 3, md: 3.5 },
            pb: { xs: 2.5, sm: 3, md: 3.5 },
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}>
          Here you can find and manage all running events for setup and tracking.
        </Typography>
      </Box>

      {/* Events List Section */}
      <Box
        sx={{
          mb: { xs: 2.5, sm: 3, md: 3.5 },
        }}>
        {/* Section Header with Controls */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: { xs: 2, sm: 2.5, md: 3 },
            mb: { xs: 2.5, sm: 3, md: 3.5 },
          }}>
          <Typography
            variant="h6"
            component="h2"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
              lineHeight: 1.3,
            }}>
            Events List
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2, sm: 2.5, md: 3 },
              width: { xs: '100%', sm: 'auto' },
              alignItems: { xs: 'stretch', sm: 'center' },
              flexShrink: 0,
            }}>
            {/* Search Input */}
            <TextField
              placeholder="Search event name"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value)
                setPage(0)
              }}
              size="small"
              sx={{
                width: { xs: '100%', sm: 280, md: 320 },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                  borderRadius: 1,
                  fontSize: {
                    xs: '0.8125rem',
                    sm: '0.875rem',
                    md: '0.9375rem',
                  },
                  '& fieldset': {
                    borderColor: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.23)'
                        : 'rgba(255, 255, 255, 0.23)',
                  },
                  '&:hover fieldset': {
                    borderColor: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.87)'
                        : 'rgba(255, 255, 255, 0.87)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  color: 'text.primary',
                  py: { xs: 1, sm: 1.125, md: 1.25 },
                  px: { xs: 1.25, sm: 1.5, md: 1.75 },
                  '&::placeholder': {
                    color: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.5)'
                        : 'rgba(255, 255, 255, 0.5)',
                    opacity: 1,
                  },
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ mr: { xs: 1, sm: 1.25, md: 1.5 } }}>
                    <SearchIcon
                      sx={{
                        color: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(0, 0, 0, 0.54)'
                            : 'rgba(255, 255, 255, 0.54)',
                        fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                      }}
                    />
                  </InputAdornment>
                ),
              }}
            />

            {/* Create Event Button */}
            {user.role === 'Admin' && (
              <Button
                variant="contained"
                color="primary"
                component={Linkreact}
                to="/event/create"
                startIcon={<NoteAddTwoToneIcon />}
                sx={{
                  borderRadius: 1,
                  fontWeight: 600,
                  fontSize: {
                    xs: '0.8125rem',
                    sm: '0.875rem',
                    md: '0.9375rem',
                  },
                  textTransform: 'none',
                  py: { xs: 1, sm: 1.125, md: 1.25 },
                  px: { xs: 2, sm: 2.5, md: 3 },
                  whiteSpace: 'nowrap',
                  width: { xs: '100%', sm: 'auto' },
                  boxShadow: (theme) =>
                    theme.palette.mode === 'light'
                      ? '0 2px 4px rgba(0, 0, 0, 0.1)'
                      : '0 2px 4px rgba(0, 0, 0, 0.3)',
                  '&:hover': {
                    boxShadow: (theme) =>
                      theme.palette.mode === 'light'
                        ? '0 4px 8px rgba(0, 0, 0, 0.15)'
                        : '0 4px 8px rgba(0, 0, 0, 0.4)',
                  },
                }}>
                Create Event
              </Button>
            )}
          </Box>
        </Box>

        {/* Events Table */}
        <EventsTable
          events={events}
          loading={events.loading}
          paginatedEvents={paginatedEvents}
          page={page}
          totalPages={totalPages}
          onPageChange={handleChangePage}
          getPageNumbers={getPageNumbers}
        />
      </Box>
    </Box>
  )
}

export default EventsPage
